import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { packageService, Package } from '../../services/packages';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../services/api';
import Input from '../../components/base/Input';
import Button from '../../components/base/Button';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function PackageCheckout() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [pkg, setPkg] = useState<Package | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Form fields
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        state: '',
        lga: '',
        passportNumber: '',
        dob: ''
    });

    useEffect(() => {
        if (user) {
            setFormData(prev => ({
                ...prev,
                name: user.displayName || '',
                email: user.email || ''
            }));
        }
    }, [user]);

    useEffect(() => {
        const fetchPackage = async () => {
            try {
                if (!id) return;
                // Fetch single package by ID
                const data = await apiFetch(`/packages/${id}`);
                setPkg(data);
            } catch (err) {
                console.error("Error fetching package details:", err);
                setError("Failed to load package details.");
            } finally {
                setLoading(false);
            }
        };
        fetchPackage();
    }, [id]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            // Create a pending booking for this package
            const bookingData = {
                service_type: 'package',
                status: 'pending',
                amount: pkg?.price,
                items: [{
                    type: 'package',
                    details: {
                        package_id: pkg?.id,
                        title: pkg?.title,
                        type: pkg?.type,
                        dates: pkg?.dates
                    },
                    amount: pkg?.price
                }],
                travelers: [{
                    first_name: formData.name.split(' ')[0] || '',
                    last_name: formData.name.split(' ').slice(1).join(' ') || '',
                    date_of_birth: formData.dob,
                    passport_number: formData.passportNumber,
                    contact_phone: formData.phone,
                    contact_email: formData.email,
                    address: formData.address,
                    state: formData.state,
                    lga: formData.lga
                }]
            };

            await apiFetch('/bookings', {
                method: 'POST',
                body: JSON.stringify(bookingData)
            });

            // Redirect to dashboard where they can see the booking and receipt
            navigate('/dashboard', { state: { message: 'Booking submitted successfully!' } });
        } catch (err: any) {
            console.error("Booking error:", err);
            setError(err.message || 'Failed to submit booking');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col">
                <Header />
                <div className="flex-1 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-logo-gold-600"></div>
                </div>
            </div>
        );
    }

    if (!pkg) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col">
                <Header />
                <div className="flex-1 flex flex-col items-center justify-center">
                    <h2 className="text-2xl font-bold text-slate-800 mb-4">Package not found</h2>
                    <Button onClick={() => navigate('/hajj-umrah')}>Back to Packages</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
            <Header />
            
            <main className="flex-1 py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
                <div className="flex flex-col md:flex-row gap-8">
                    
                    {/* Left Column: Form */}
                    <div className="flex-1 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="p-8">
                            <h2 className="text-3xl font-bold text-slate-900 mb-2">Complete Your Booking</h2>
                            <p className="text-slate-500 mb-8">Please provide your details below to secure your spot for {pkg.title}.</p>
                            
                            {error && (
                                <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg text-sm font-medium border border-red-100">
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">Full Name</label>
                                        <Input name="name" value={formData.name} onChange={handleChange} required placeholder="Enter full name" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                                        <Input type="email" name="email" value={formData.email} onChange={handleChange} required disabled={!!user?.email} placeholder="Enter email" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number</label>
                                        <Input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="e.g. +234..." />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">Date of Birth</label>
                                        <Input type="date" name="dob" value={formData.dob} onChange={handleChange} required />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Home Address</label>
                                    <Input name="address" value={formData.address} onChange={handleChange} required placeholder="Street address" />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">State</label>
                                        <Input name="state" value={formData.state} onChange={handleChange} required placeholder="e.g. Lagos" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">LGA (Local Government Area)</label>
                                        <Input name="lga" value={formData.lga} onChange={handleChange} required placeholder="e.g. Ikeja" />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Passport Number</label>
                                    <Input name="passportNumber" value={formData.passportNumber} onChange={handleChange} required placeholder="Enter passport number" />
                                </div>

                                <div className="pt-4">
                                    <Button type="submit" disabled={submitting} className="w-full bg-logo-gold-600 hover:bg-logo-gold-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-logo-gold-600/30 transition-all text-lg">
                                        {submitting ? 'Processing...' : 'Confirm & Book Package'}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Right Column: Order Summary */}
                    <div className="w-full md:w-96">
                        <div className="bg-slate-900 rounded-2xl shadow-xl border border-slate-800 overflow-hidden sticky top-24 text-white">
                            <div className="h-48 relative">
                                <img src={pkg.image_url} alt={pkg.title} className="w-full h-full object-cover opacity-60 mix-blend-overlay" />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent"></div>
                                <div className="absolute bottom-4 left-6 right-6">
                                    <span className="inline-block px-3 py-1 bg-logo-gold-500 text-slate-900 text-xs font-bold rounded-full uppercase tracking-wider mb-2">
                                        {pkg.type} Package
                                    </span>
                                    <h3 className="text-xl font-bold">{pkg.title}</h3>
                                </div>
                            </div>
                            
                            <div className="p-6">
                                <div className="space-y-4 mb-6">
                                    <div className="flex items-center text-slate-300">
                                        <i className="ri-calendar-event-line mr-3 text-logo-gold-400"></i>
                                        <span className="text-sm">{pkg.dates}</span>
                                    </div>
                                    <div className="flex items-center text-slate-300">
                                        <i className="ri-time-line mr-3 text-logo-gold-400"></i>
                                        <span className="text-sm">{pkg.duration}</span>
                                    </div>
                                </div>
                                
                                <div className="border-t border-slate-700 pt-6">
                                    <div className="flex justify-between items-end">
                                        <span className="text-slate-400">Total Price</span>
                                        <span className="text-3xl font-extrabold text-logo-gold-400">₦{pkg.price.toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </main>
            
            <Footer />
        </div>
    );
}
